import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CaseBattlesCreateService } from '../services/case-battles-create.service';
import { CaseBattlesService } from '../case-battles.service';
import {
  CaseBattleAnimationEnum,
  CaseBattlesSlotsVariants,
  ICaseBattle,
  ICaseBattleRound,
} from '../interfaces/case-battles.interface';
import { CbBattleHeaderComponent } from '../cb-battle-header/cb-battle-header.component';
import { FairnessButtonComponent } from '../../../core/components/fairness-button/fairness-button.component';
import { SvgIconComponent } from 'angular-svg-icon';
import { CbBattleSpinner2Component } from '../cb-battle-spinner-2/cb-battle-spinner-2.component';
import { CbBattleMiddleWaitingPlayerComponent } from '../cb-battle-middle-waiting-player/cb-battle-middle-waiting-player.component';
import { CbBattleMiddleResultsPlayerComponent } from '../cb-battle-middle-results-player/cb-battle-middle-results-player.component';
import { fadeInAnimation } from '@core/utils/animations.util';
import { CbBattlePlayer2Component } from '../cb-battle-player-2/cb-battle-player-2.component';
import { sleep, sleepWithId } from '@core/utils/sleep.util';
import { map, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CbBattleFinishedComponent } from '../cb-battle-finished/cb-battle-finished.component';
import { CbBattlePlayerSkinsComponent } from '../cb-battle-player-skins/cb-battle-player-skins.component';
import { CbBattleTieComponent } from '../cb-battle-tie/cb-battle-tie.component';
import { BalanceType } from '@core/types/balance.type';
import { SessionService } from '@core/services/session.service';
import { CasesService } from '@features/cases/services/cases.service';
import { SocketService } from '@core/services/socket.service';

@Component({
  selector: 'app-cb-battle',
  templateUrl: './cb-battle.component.html',
  styleUrls: ['./cb-battle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CbBattleHeaderComponent,
    FairnessButtonComponent,
    SvgIconComponent,
    RouterLink,
    CbBattleSpinner2Component,
    CbBattleMiddleWaitingPlayerComponent,
    CbBattleMiddleResultsPlayerComponent,
    CbBattlePlayer2Component,
    CbBattleFinishedComponent,
    CbBattleTieComponent,
    CbBattlePlayerSkinsComponent,
  ],
  animations: [fadeInAnimation],
})
export class CbBattleComponent {
  public caseBattlesService = inject(CaseBattlesService);
  private socketService = inject(SocketService);
  private router = inject(Router);
  private caseBattlesCreateService = inject(CaseBattlesCreateService);
  private toastrService = inject(ToastrService);
  private destroyRef = inject(DestroyRef);
  public sessionService = inject(SessionService);
  private casesService = inject(CasesService);

  public battle = signal<ICaseBattle | null>(null);
  public slots = computed<CaseBattlesSlotsVariants[]>(() =>
    Array.from(
      { length: this.battle()?.max_participants ?? 0 },
      (_, i) => (i + 1) as CaseBattlesSlotsVariants
    )
  );
  public battleColor = computed(() => {
    return this.battle()?.mode === 'nuclear'
      ? 'var(--case-battle-nuclear)'
      : this.battle()?.mode === 'equity'
      ? 'var(--case-battle-equity)'
      : this.battle()?.mode === 'crazy'
      ? 'var(--case-battle-crazy)'
      : this.battle()?.mode === 'normal'
      ? 'var(--case-battle-normal)'
      : null;
  });

  player = computed<{
    isParticipant: boolean;
    isWinner: boolean;
    balance: BalanceType;
    isHost: boolean;
  }>(() => {
    const isParticipant = this.battle()?.participants.find(
      (participant) =>
        participant.steamid === this.sessionService.profile()?.steamid
    );
    const isWinner = this.battle()?.winners.find(
      (winner) => winner.steamid === this.sessionService.profile()?.steamid
    );
    const balance = isParticipant?.balance ?? this.sessionService.balanceType();
    return {
      isParticipant: !!isParticipant,
      isWinner: !!isWinner,
      balance: balance,
      isHost: isParticipant?.is_host ?? false,
    };
  });

  public animation = signal<keyof typeof CaseBattleAnimationEnum | null>(null);

  spinnerComponents = viewChildren(CbBattleSpinner2Component);
  players = viewChildren(CbBattlePlayer2Component);
  playersSkins = viewChildren(CbBattlePlayerSkinsComponent);
  battleHeader = viewChild(CbBattleHeaderComponent);

  battleUID = signal<string | null>(null);

  timeoutIds = signal<number[]>([]);

  isNavigationFromList: boolean = false;
  isSkipTie: boolean = false;
  isRollToResults: boolean = false;

  isResetActiveBattle: boolean = false;
  isBattleCancel = signal<boolean>(false);

  constructor() {
    this.socketService.socket.on('connect', () => {
      if (this.battleUID()) {
        this.caseBattlesService.subscribeBattle(this.battleUID()!);
      }
    });

    const subscribeBattleEffect = effect(() => {
      const uid = this.battleUID();
      if (uid) {
        this.caseBattlesService.subscribeBattle(uid);
        // subscribeBattleEffect.destroy();
      }
    });

    this.destroyRef.onDestroy(() => {
      this.resetTimeoutIds();
    });
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e) => e.url.split('/').at(-1)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((route) => {
        if (route) {
          const state = this.router.currentNavigation()?.extras.state;
          if (state?.['navigationFromList']) {
            this.isNavigationFromList = true;
          }
          if (state?.['skipTie']) {
            this.isSkipTie = true;
          }
          if (state?.['isResetActiveBattle']) {
            this.isResetActiveBattle = true;
            //todo later
            this.battleUID.set(null);
            this.battle.set(null);
            this.resetTimeoutIds();
            this.animation.set(null);
            this.players().forEach((player) => {
              player.resetDropCosts();
            });
            this.playersSkins().forEach((player) => {
              player.resetDrops();
            });
            this.battleHeader()?.resetCosts();

            this.isResetActiveBattle = false;
            this.battleUID.set(route);
          } else {
            this.battleUID.set(route);
          }
        }
      });

    effect(() => {
      console.log(this.animation());
    });

    effect(async () => {
      const battleLink = this.battleUID();
      const caseBattlesListLoading =
        this.caseBattlesService.caseBattlesListLoading();

      const casesListLoading = this.casesService.casesList().length === 0;
      // cases is required for fill spinner
      if (this.isBattleCancel() || casesListLoading) return;

      if (!battleLink) {
        this.toastrService.error('Battle Lobby Not Found');
        this.router.navigate(['/battles']);
        return;
      }

      if (caseBattlesListLoading) return;

      const battle =
        this.caseBattlesService.findBattleByUID(battleLink) ??
        (await this.caseBattlesService.getBattle(battleLink));

      const isBattle = !!battle && battle?.uid === battleLink;

      if (!isBattle) {
        this.router.navigate(['/battles']);
        this.toastrService.error('Battle Lobby Not Found');
        return;
      }

      const isSkipRoundOnListNavigation =
        this.isNavigationFromList && battle.status === 'running';
      const isSkipRoundOnPageRefresh =
        !battle._roundsUpdate && battle.status === 'running';

      if (isSkipRoundOnPageRefresh) {
        battle._roundsUpdate = true;
      }

      if (!this.battle()?._replay) {
        this.battle.set(battle);
      }

      if (this.isNavigationFromList) {
        this.isNavigationFromList = false;
      }
      if (isSkipRoundOnListNavigation || isSkipRoundOnPageRefresh) {
        this.isRollToResults = true;
      }
    });

    effect(async () => {
      if (
        this.battle()?.status === 'open' &&
        this.animation() !== 'open' &&
        this.battle()?.uid === this.battleUID()
      ) {
        this.animation.set('open');
      }
    });

    effect(async () => {
      const battleSettings = this.caseBattlesService.getBattleSettings(
        this.battle()
      );
      if (
        this.battle()?._roundsUpdate &&
        this.battle()?.status === 'running' &&
        this.battle()?.uid === this.battleUID()
      ) {
        this.animation.set('running');
        const { promise, timeoutId } = await sleepWithId(
          battleSettings.ROLLING_CLOSER_TIMEOUT + battleSettings.ROLLING_TIMEOUT
        );

        if (timeoutId) {
          this.timeoutIds.update((prev) => [...prev, timeoutId]);
        }
        await promise;

        this.animation.set('round-results');
      }
    });

    effect(async () => {
      if (
        this.player().isParticipant &&
        !this.battle()?._replay &&
        this.player().isWinner &&
        this.animation() === 'battle-results'
      ) {
        await sleep(300);
        const balance = this.player().balance;
        console.log('🚀 ~ CbBattleComponent ~ constructor ~ balance:', balance);
        await this.sessionService.updateBalance(balance);
      }
    });

    effect(async () => {
      const battleSettings = this.caseBattlesService.getBattleSettings(
        this.battle()
      );
      if (
        this.battle()?.status === 'finished' &&
        this.battle()?.uid === this.battleUID() &&
        this.animation() !== 'finished' &&
        this.animation() !== 'tie' &&
        this.animation() !== 'battle-results'
      ) {
        if (
          this.battle()?.tie &&
          !this.battle()?._skip_tie &&
          !this.isSkipTie
        ) {
          this.animation.set('tie');
          const { promise, timeoutId } = await sleepWithId(
            battleSettings.TIE_TOTAL_TIMEOUT
          );
          if (timeoutId) {
            this.timeoutIds.update((prev) => [...prev, timeoutId]);
          }
          await promise;
        }

        this.animation.set('battle-results');

        const { promise, timeoutId } = await sleepWithId(
          battleSettings.BATTLE_RESULTS_TIMEOUT
        );
        if (timeoutId) {
          this.timeoutIds.update((prev) => [...prev, timeoutId]);
        }
        await promise;

        this.animation.set('finished');
      }
    });

    afterRenderEffect(async () => {
      const battle = this.battle();
      if (
        !!battle &&
        battle._roundsUpdate &&
        battle.status === 'running' &&
        this.animation() === 'running' &&
        !this.isRollToResults &&
        this.spinnerComponents().length
      ) {
        console.log('rollSpinners');
        await this.rollSpinners(battle.rounds);
      }
    });

    const runningInitSpinners = afterRenderEffect(async () => {
      const battle = this.battle();
      if (
        !!battle &&
        battle.status === 'running' &&
        this.isRollToResults &&
        this.spinnerComponents().length &&
        this.playersSkins().length &&
        this.players().length
      ) {
        console.log('init spinners after navigate already running battle');
        for (let i = 0; i < this.spinnerComponents().length; i++) {
          this.spinnerComponents()[i].rollWithoutAnimation(
            battle.rounds[battle.rounds.length - 1].drops[i]
          );
        }

        for (let i = 0; i < this.players().length; i++) {
          this.players()[i].setDropsCosts(battle.rounds);
        }

        for (let i = 0; i < this.playersSkins().length; i++) {
          this.playersSkins()[i].setDrops(
            this.playersSkins()[i].getDroppedItemsBySlot(battle)
          );
        }
        this.isRollToResults = false;
        runningInitSpinners.destroy();
      }
    });
  }

  private async rollSpinners(rounds: ICaseBattleRound[]) {
    const winItems = rounds[rounds.length - 1].drops;

    try {
      const spinnersComponents = this.spinnerComponents();

      const rollPromises = [];
      for (let i = 0; i < spinnersComponents.length; i++) {
        rollPromises.push(spinnersComponents[i].roll(winItems[i], winItems));
      }

      await Promise.all(rollPromises);
    } catch (error) {
      console.log('🚀 ~ CasesUnboxingComponent ~ rollSpinners ~ error:', error);
    }
  }

  public async reCreateBattle() {
    if (!this.battle()) {
      this.toastrService.error('Failed to re-create battle');
      return;
    }

    try {
      const battle = await this.caseBattlesCreateService.reCreateBattle(
        this.battle()!
      );

      if (battle) {
        this.isSkipTie = false;
        this.isRollToResults = false;
        this.isNavigationFromList = false;
        this.isResetActiveBattle = false;
        this.resetTimeoutIds();
        await sleep(100);

        this.animation.set('open');
        this.battle.set(battle);
        this.battleUID.set(battle.uid);

        this.router.navigate(['/battles', battle.uid]);

        this.battleHeader()?.resetCosts();
        this.players().forEach((player) => {
          player.resetDropCosts();
        });
        this.playersSkins().forEach((player) => {
          player.resetDrops();
        });
      }

      this.toastrService.success('Battle successfully re-created');
    } catch (error) {
      console.log('🚀 ~ CbBattleComponent ~ reCreateBattle ~ error:', error);
    }
  }

  public async rePlayBattle() {
    const battle = this.battle()!;

    if (!battle) {
      this.toastrService.error('Failed to re-play battle');
      return;
    }

    this.toastrService.success('Battle replay started');
    this.resetTimeoutIds();

    const finalBattle: ICaseBattle = {
      ...battle,
      rounds: [],
      winners: [],
      status: 'open',
      tie: false,
      tiebreaker: [],
      tie_array: [],
      roundsNumber: 0,
      _roundsNumber: battle.roundsNumber,
      _rounds: battle.rounds,
      _winners: battle.winners,
      _status: battle.status,
      _tie: battle.tie,
      _tiebreaker: battle.tiebreaker,
      _tie_array: battle.tie_array,
      _end: battle.end,
      _replay: true,
    };
    this.battle.set(finalBattle);

    this.battleHeader()?.resetCosts();
    this.players().forEach((player) => {
      player.resetDropCosts();
    });
    this.playersSkins().forEach((player) => {
      player.resetDrops();
    });

    const {
      BATTLE_RESULTS_TIMEOUT,
      ROLLING_CLOSER_TIMEOUT,
      ROLLING_TIMEOUT,
      ROUND_RESULTS_TIMEOUT,
      TIE_TOTAL_TIMEOUT,
    } = this.caseBattlesService.getBattleSettings(battle);

    const totalRounds = finalBattle._roundsNumber;
    const rounds = Array.from({ length: totalRounds }).map((_, i) => i + 1);

    for await (const round of rounds) {
      this.animation.set('running');
      const isLast = round === totalRounds;

      const battleRound: ICaseBattle = {
        ...finalBattle,
        status: 'running',
        rounds: finalBattle._rounds.slice(0, round),
        roundsNumber: round,
        _roundsUpdate: true,
        _replay: true,
      };
      this.battle.update((prev) => {
        return {
          ...prev,
          ...battleRound,
        };
      });
      const { promise, timeoutId } = await sleepWithId(
        ROLLING_CLOSER_TIMEOUT + ROLLING_TIMEOUT
      );
      if (timeoutId) {
        this.timeoutIds.update((prev) => [...prev, timeoutId]);
      }
      await promise;
      this.animation.set('round-results');
      const { promise: promise2, timeoutId: timeoutId2 } = await sleepWithId(
        ROUND_RESULTS_TIMEOUT
      );
      if (timeoutId2) {
        this.timeoutIds.update((prev) => [...prev, timeoutId2]);
      }
      await promise2;

      if (isLast) {
        this.battle.set({ ...battle, _replay: true });
        if (battle.tie) {
          this.animation.set('tie');
          const { promise: promise3, timeoutId: timeoutId3 } =
            await sleepWithId(TIE_TOTAL_TIMEOUT);
          if (timeoutId3) {
            this.timeoutIds.update((prev) => [...prev, timeoutId3]);
          }
          await promise3;
        }

        this.animation.set('battle-results');
        const { promise: promise4, timeoutId: timeoutId4 } = await sleepWithId(
          BATTLE_RESULTS_TIMEOUT
        );
        if (timeoutId4) {
          this.timeoutIds.update((prev) => [...prev, timeoutId4]);
        }
        await promise4;
        this.animation.set('finished');
      }
    }
  }

  private resetTimeoutIds() {
    this.timeoutIds().forEach((id) => {
      clearTimeout(id);
    });
    this.timeoutIds.set([]);
  }

  public async handleCancelBattle() {
    this.isBattleCancel.set(true);
    try {
      await this.caseBattlesService.cancelBattle(this.battle()?.uid ?? '');
      this.router.navigate(['/battles']);
      await this.sessionService.updateBalance(this.player().balance);
      this.toastrService.success('Battle cancelled successfully');
    } catch (error) {
      this.toastrService.error('Failed to cancel battle');
    } finally {
      this.isBattleCancel.set(false);
    }
  }
}
